import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { SubmitHandler } from 'react-hook-form';
import { Form, FormField, SubmitButton, TextInput } from 'components/Forms';
import { Heading } from 'components/Typography';
import { ContactInfoContext, withContactInfoMachineState } from 'domains/signup/contactInfo/data/ContactInfoContext';
import { TFunction } from 'i18next';

type EnterNameForm = {
  firstName: string;
  lastName: string;
};

const getFormSchema = (t: TFunction): yup.ObjectSchema<EnterNameForm> =>
  yup
    .object({
      firstName: yup.string().required(t('form.validation.fieldIsRequired')),
      lastName: yup.string().required(t('form.validation.fieldIsRequired')),
    })
    .required();

type Props = {
  defaultValues?: {
    firstName?: string;
    lastName?: string;
  };
  onSubmit: SubmitHandler<EnterNameForm>;
};

const InfoEnterName: React.FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <>
      <Form
        onSubmit={onSubmit}
        schema={getFormSchema(t)}
        defaultValues={defaultValues}
        validationMode="all"
        flashErrors={false}
      >
        <Heading level="h2" className="mb-64">
          {t(`signup.steps.enterName.title`)}
        </Heading>
        <div className="gap-24 mb-64 grid grid-cols-2 sm-d:grid-rows-2 sm-d:grid-cols-1">
          <FormField
            data-testid="first-name-input"
            component={TextInput}
            name="firstName"
            disabled={false}
            label={t(`signup.steps.enterName.firstName.label`)}
          />
          <FormField
            data-testid="last-name-input"
            component={TextInput}
            name="lastName"
            disabled={false}
            label={t(`signup.steps.enterName.lastName.label`)}
          />
        </div>

        <SubmitButton
          data-testid="name-submit-button"
          className="sm-d:w-full"
          size="lg"
          disableIfInvalid
          willRedirect={false}
        >
          {t('signup.steps.enterName.submit')}
        </SubmitButton>
      </Form>
    </>
  );
};

const ContactInfoWrapper = () => {
  const data = ContactInfoContext.useSelector(({ context: { firstName, lastName } }) => ({
    firstName,
    lastName,
  }));
  const { send } = ContactInfoContext.useActorRef();

  const onSubmit: SubmitHandler<EnterNameForm> = useCallback(
    ({ firstName, lastName }) => {
      send({
        type: 'submitName',
        firstName,
        lastName,
      });
    },
    [send],
  );

  return (
    <InfoEnterName
      defaultValues={{
        firstName: data.firstName,
        lastName: data.lastName,
      }}
      onSubmit={onSubmit}
    />
  );
};

export const ContactInfoEnterName = withContactInfoMachineState(ContactInfoWrapper, 'enterName');
